import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { ChevronLeft, ChevronRight, Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { EmptyState } from "@/components/empty-state";
import { cn } from "@/lib/utils";
import {
  addDays,
  addWeeks,
  dateKey,
  endOfDay,
  endOfWeek,
  parseDateKey,
  startOfDay,
  startOfWeek,
  subWeeks,
  todayKey,
} from "@/lib/date";
import { useAppointments } from "@/features/appointment/api";
import { useBrokers } from "@/features/appointment/hooks/use-brokers";
import { useLeads } from "@/features/lead/api";
import { useMediaQuery } from "@/features/lead/hooks/use-media-query";
import { ALL_VALUE } from "@/features/appointment/constants";
import { AppointmentAgendaView } from "@/features/appointment/components/appointment-agenda-view";
import { AppointmentWeekView } from "@/features/appointment/components/appointment-week-view";
import { AppointmentFilters } from "@/features/appointment/components/appointment-filters";
import { AppointmentForm } from "@/features/appointment/components/appointment-form";
import { AppointmentMobileView } from "@/features/appointment/components/appointment-mobile-view";
import {
  AppointmentAgendaSkeleton,
  AppointmentWeekSkeleton,
} from "@/features/appointment/components/appointment-skeleton";
import type {
  Appointment,
  AppointmentListQuery,
  AppointmentStatus,
  AppointmentView,
} from "@/features/appointment/types";

function parseView(raw: string | null, fallback: AppointmentView): AppointmentView {
  return raw === "agenda" || raw === "week" || raw === "day" ? raw : fallback;
}

function isStatus(value: string): value is AppointmentStatus {
  return (
    value === "SCHEDULED" ||
    value === "COMPLETED" ||
    value === "CANCELED" ||
    value === "NO_SHOW"
  );
}

export default function AppointmentCalendarPage() {
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const isPhone = !isDesktop;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Resolve view + date from URL. Defaults: "day" on phone (matches
  // the new mobile redesign), "week" on desktop. Date = today.
  const view = parseView(
    searchParams.get("view"),
    isPhone ? "day" : "week",
  );
  const dateParam = searchParams.get("date") ?? todayKey();
  const anchorDate = useMemo(
    () => parseDateKey(dateParam) ?? new Date(),
    [dateParam],
  );

  const brokerParam = searchParams.get("brokerId") ?? ALL_VALUE;
  const statusParam = searchParams.get("status") ?? ALL_VALUE;

  // Compute the from/to range we ask the server for. The three views
  // need different windows:
  //   - "day"    → just the anchor's day (mobile Ngày)
  //   - "agenda" → 14 days from anchor (desktop list mode)
  //   - "week"   → the calendar week containing the anchor
  // We always fetch the whole week on mobile so switching from Ngày →
  // Tuần doesn't trigger a refetch and the date strip's "this day has
  // appointments" dot can colour correctly across all 7 cells.
  const range = useMemo(() => {
    if (view === "agenda") {
      const start = startOfDay(anchorDate);
      const end = endOfDay(addDays(start, 13));
      return { from: start.toISOString(), to: end.toISOString() };
    }
    // Both "day" and "week" fetch the full week containing the anchor.
    const start = startOfDay(startOfWeek(anchorDate, { weekStartsOn: 1 }));
    const end = endOfDay(endOfWeek(anchorDate, { weekStartsOn: 1 }));
    return { from: start.toISOString(), to: end.toISOString() };
  }, [view, anchorDate]);

  const listQuery: AppointmentListQuery = useMemo(() => {
    const q: AppointmentListQuery = { from: range.from, to: range.to };
    if (brokerParam !== ALL_VALUE) {
      const id = Number.parseInt(brokerParam, 10);
      if (!Number.isNaN(id)) q.brokerId = id;
    }
    if (statusParam !== ALL_VALUE && isStatus(statusParam)) {
      q.status = statusParam;
    }
    return q;
  }, [range, brokerParam, statusParam]);

  const appointmentsQuery = useAppointments(listQuery);
  const leadsQuery = useLeads({});
  const brokersQuery = useBrokers();

  const [creating, setCreating] = useState(false);

  // Selecting an appointment from any list/grid surface routes to the
  // full-page detail at `/appointments/:id`. The old in-page sheet has
  // been removed — the detail page owns its own chrome, edit form, and
  // status mutations.
  const openAppointment = (appointment: Appointment) => {
    navigate(`/appointments/${appointment.id}`);
  };

  // URL-update helpers — push search params without nuking the others.
  const updateParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (value === null || value === "") next.delete(key);
    else next.set(key, value);
    setSearchParams(next, { replace: true });
  };

  const handleViewChange = (next: AppointmentView) => updateParam("view", next);
  const handleDateChange = (next: Date) =>
    updateParam("date", dateKey(next));
  const handleTodayClick = () => updateParam("date", todayKey());
  const handlePrev = () => {
    handleDateChange(
      view === "week" ? subWeeks(anchorDate, 1) : addDays(anchorDate, -7),
    );
  };
  const handleNext = () => {
    handleDateChange(
      view === "week" ? addWeeks(anchorDate, 1) : addDays(anchorDate, 7),
    );
  };

  const weekLabel = (() => {
    const monthName = anchorDate.toLocaleDateString("vi-VN", { month: "long", year: "numeric" });
    const weekNumber = Math.ceil(
      ((anchorDate.getTime() - new Date(anchorDate.getFullYear(), 0, 1).getTime()) /
        (7 * 24 * 60 * 60 * 1000)) + 1,
    );
    return `${monthName.charAt(0).toUpperCase() + monthName.slice(1)} · Tuần ${weekNumber}`;
  })();
  const totalThisWeek = appointmentsQuery.data?.length ?? 0;

  // Mobile redesign — entirely separate shell (no desktop header, no
  // inline filters, no view-toggle buttons). Renders the new
  // AppointmentMobileView, which owns its own Ngày/Tuần toggle and
  // date strip but defers data fetching + URL state to this page.
  if (isPhone) {
    return (
      <div className="flex min-h-svh flex-col bg-background">
        {appointmentsQuery.isError ? (
          <div className="px-4 pt-8">
            <EmptyState
              title="Không thể tải lịch hẹn"
              description="Vui lòng kiểm tra kết nối mạng và thử lại."
              action={
                <Button
                  variant="outline"
                  onClick={() => void appointmentsQuery.refetch()}
                >
                  Thử lại
                </Button>
              }
            />
          </div>
        ) : appointmentsQuery.isPending ? (
          <AppointmentAgendaSkeleton />
        ) : (
          <AppointmentMobileView
            // Coerce the broader AppointmentView union down to the
            // mobile-only subset before handing off — the page still
            // owns the canonical "view" state, so a stale `?view=
            // agenda` query param doesn't break the mobile rendering.
            view={view === "week" ? "week" : "day"}
            anchorDate={anchorDate}
            appointments={appointmentsQuery.data}
            leads={leadsQuery.data ?? []}
            brokers={brokersQuery.data ?? []}
            onViewChange={handleViewChange}
            onDateChange={handleDateChange}
            onSelectAppointment={openAppointment}
          />
        )}

        {/* FAB stays on mobile — the new top-bar design dropped the
            "Yêu cầu xem nhà" button, so the FAB is the primary
            create affordance. */}
        <Button
          onClick={() => setCreating(true)}
          size="icon"
          aria-label="Yêu cầu xem nhà"
          className="fixed right-5 bottom-20 z-30 size-14 rounded-full shadow-lg sm:hidden"
        >
          <Plus aria-hidden="true" className="size-6" />
        </Button>

        <CreateAppointmentSurface
          open={creating}
          isDesktop={isDesktop}
          onClose={() => setCreating(false)}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <header className="flex flex-wrap items-center gap-3 border-b border-border bg-card px-6 py-3">
        <div className="flex-1">
          <p className="font-mono text-[12px] text-ink-faint">{weekLabel}</p>
          <h1 className="flex items-baseline gap-2 text-[28px] font-bold tracking-tight">
            Lịch hẹn
            <span className="font-mono text-sm font-medium text-muted-foreground">
              {totalThisWeek} trong tuần
            </span>
          </h1>
        </div>
        <div className="flex gap-1 rounded-lg border border-border bg-background p-1">
          <button
            type="button"
            onClick={() => handleViewChange("agenda")}
            className={cn(
              "rounded-md px-3 py-1 text-[12px] font-semibold",
              view === "agenda"
                ? "bg-foreground text-background"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            Lịch trình
          </button>
          <button
            type="button"
            onClick={() => handleViewChange("week")}
            className={cn(
              "rounded-md px-3 py-1 text-[12px] font-semibold",
              view === "week"
                ? "bg-foreground text-background"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            Tuần
          </button>
        </div>
        <div className="flex gap-1">
          <Button
            type="button"
            size="icon-sm"
            variant="outline"
            onClick={handlePrev}
            aria-label="Tuần trước"
          >
            <ChevronLeft className="size-3.5" aria-hidden="true" />
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={handleTodayClick}>
            Hôm nay
          </Button>
          <Button
            type="button"
            size="icon-sm"
            variant="outline"
            onClick={handleNext}
            aria-label="Tuần sau"
          >
            <ChevronRight className="size-3.5" aria-hidden="true" />
          </Button>
        </div>
        <Button
          size="sm"
          onClick={() => setCreating(true)}
          className="bg-foreground text-background hover:bg-foreground/90"
        >
          <Plus aria-hidden="true" />
          Yêu cầu xem nhà
        </Button>
      </header>

      <div className="flex flex-wrap items-center gap-3 border-b border-border px-6 py-3">
        <AppointmentFilters
          brokerId={brokerParam}
          status={statusParam}
          onBrokerChange={(value) =>
            updateParam("brokerId", value === ALL_VALUE ? null : value)
          }
          onStatusChange={(value) =>
            updateParam("status", value === ALL_VALUE ? null : value)
          }
        />
      </div>

      <div className="px-6 py-4">


      {appointmentsQuery.isError ? (
        <EmptyState
          title="Không thể tải lịch hẹn"
          description="Vui lòng kiểm tra kết nối mạng và thử lại."
          action={
            <Button
              variant="outline"
              onClick={() => void appointmentsQuery.refetch()}
            >
              Thử lại
            </Button>
          }
        />
      ) : appointmentsQuery.isPending ? (
        view === "agenda" ? (
          <AppointmentAgendaSkeleton />
        ) : (
          <AppointmentWeekSkeleton />
        )
      ) : view === "agenda" ? (
        <AppointmentAgendaView
          appointments={appointmentsQuery.data}
          leads={leadsQuery.data ?? []}
          brokers={brokersQuery.data ?? []}
          onSelectAppointment={openAppointment}
          onCreate={() => setCreating(true)}
        />
      ) : (
        <AppointmentWeekView
          anchorDate={anchorDate}
          appointments={appointmentsQuery.data}
          leads={leadsQuery.data ?? []}
          brokers={brokersQuery.data ?? []}
          onSelectAppointment={openAppointment}
        />
      )}

      {/* Mobile FAB */}
      <Button
        onClick={() => setCreating(true)}
        size="icon"
        aria-label="Yêu cầu xem nhà"
        className="fixed bottom-5 right-5 z-30 size-14 rounded-full shadow-lg sm:hidden"
      >
        <Plus aria-hidden="true" className="size-6" />
      </Button>

      <CreateAppointmentSurface
        open={creating}
        isDesktop={isDesktop}
        onClose={() => setCreating(false)}
      />
      </div>
    </div>
  );
}

interface CreateAppointmentSurfaceProps {
  open: boolean;
  isDesktop: boolean;
  onClose: () => void;
}

/**
 * Renders the create-appointment modal (desktop) or bottom sheet (mobile).
 *
 * <p>Custom header: eyebrow "Mới · Lịch hẹn" + title "Yêu cầu xem nhà" +
 * close button on the right. Matches the design comp at
 * design/src/components/desktop/Appointments.jsx (lines 92–98).
 *
 * <p>The form renders its own footer, so the body is just the field stack.
 * The Dialog/Sheet container is flushed (`p-0`, `gap-0`) so the header,
 * body padding, and footer border line up with the design.
 */
function CreateAppointmentSurface({
  open,
  isDesktop,
  onClose,
}: CreateAppointmentSurfaceProps) {
  if (!open) return null;
  const form = (
    <div className="px-5 py-4">
      <AppointmentForm
        mode="create"
        onSuccess={onClose}
        onCancel={onClose}
      />
    </div>
  );
  const closeButton = (
    <button
      type="button"
      onClick={onClose}
      aria-label="Đóng"
      className="flex size-8 items-center justify-center rounded-lg border border-border bg-background text-muted-foreground hover:bg-muted"
    >
      <X aria-hidden="true" className="size-3.5" />
    </button>
  );
  if (isDesktop) {
    return (
      <Dialog open={open} onOpenChange={(o) => (!o ? onClose() : undefined)}>
        <DialogContent
          className="gap-0 overflow-hidden p-0 sm:max-w-[520px]"
          showCloseButton={false}
        >
          <div className="flex items-center justify-between border-b border-border px-5 py-4">
            <div>
              <p className="font-mono text-[12px] text-muted-foreground">
                Mới · Lịch hẹn
              </p>
              <DialogTitle className="mt-0.5 text-[16px] font-bold tracking-tight">
                Yêu cầu xem nhà
              </DialogTitle>
            </div>
            {closeButton}
          </div>
          {form}
        </DialogContent>
      </Dialog>
    );
  }
  return (
    <Sheet open={open} onOpenChange={(o) => (!o ? onClose() : undefined)}>
      <SheetContent
        side="bottom"
        className="max-h-[90vh] gap-0 overflow-y-auto p-0"
        showCloseButton={false}
      >
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <div>
            <p className="font-mono text-[12px] text-muted-foreground">
              Mới · Lịch hẹn
            </p>
            <SheetTitle className="mt-0.5 text-[16px] font-bold tracking-tight">
              Yêu cầu xem nhà
            </SheetTitle>
          </div>
          {closeButton}
        </div>
        {form}
      </SheetContent>
    </Sheet>
  );
}
